import React, { useState } from "react";
import { toast } from "sonner";
import { errMsg, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { ErrorBox, Field, Loading, StatusBadge } from "../../components/kit";

const BLANK = { name: "", display_name: "", headline: "", description: "", category_ids: [], area_ids: [], availability: "unavailable", status: "pending" };

function Checks({ items, value, onChange, prefix }: any) {
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x: string) => x !== id) : [...value, id]);
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it: any) => (
        <label key={it.id} className="flex items-center gap-1 rounded border border-[var(--steel)] bg-white px-2 py-1 text-xs">
          <input type="checkbox" checked={value.includes(it.id)} onChange={() => toggle(it.id)} data-testid={`${prefix}-${it.id}`} />{it.name}
        </label>
      ))}
    </div>
  );
}

export function OrgForm({ org, onSaved, onCancel }: { org?: any; onSaved: () => void; onCancel: () => void }) {
  const api = useApi();
  const { categories, areas } = useTaxonomy();
  const [v, setV] = useState<any>(org ? { ...BLANK, ...org.profile, name: org.name } : BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k: string, val: any) => setV((s: any) => ({ ...s, [k]: val }));
  const tid = org ? `ops-org-edit-${org.id}` : "ops-org-create";
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { name, display_name, headline, description, category_ids, area_ids, availability, status } = v;
    const body: any = { name, display_name, headline, description, category_ids, area_ids, availability };
    try {
      await (org ? api("put", `/ops/orgs/${org.id}`, body) : api("post", "/ops/orgs", { ...body, status }));
      toast.success(org ? "Organization saved" : "Organization created");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-3 rounded-lg border border-[var(--blue)] bg-[#f6f9ff] p-4" data-testid={tid}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Organization name (private until engagement)" id={`${tid}-name`}><input id={`${tid}-name`} required minLength={2} className="input" value={v.name} onChange={(e) => set("name", e.target.value)} data-testid={`${tid}-name-input`} /></Field>
        <Field label="Display name (shown after engagement)" id={`${tid}-dn`}><input id={`${tid}-dn`} required minLength={2} className="input" value={v.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid={`${tid}-display-input`} /></Field>
      </div>
      <Field label="Headline (private until engagement)" id={`${tid}-hl`}><input id={`${tid}-hl`} className="input" value={v.headline} onChange={(e) => set("headline", e.target.value)} data-testid={`${tid}-headline-input`} /></Field>
      <Field label="Description (private until engagement)" id={`${tid}-ds`}><textarea id={`${tid}-ds`} rows={2} className="input" value={v.description} onChange={(e) => set("description", e.target.value)} data-testid={`${tid}-description-input`} /></Field>
      <div><p className="label">Trades</p><Checks items={categories} value={v.category_ids} onChange={(x: any) => set("category_ids", x)} prefix={`${tid}-cat`} /></div>
      <div><p className="label">Service areas</p><Checks items={areas} value={v.area_ids} onChange={(x: any) => set("area_ids", x)} prefix={`${tid}-area`} /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Availability" id={`${tid}-av`}>
          <select id={`${tid}-av`} className="input" value={v.availability} onChange={(e) => set("availability", e.target.value)} data-testid={`${tid}-availability-select`}>
            <option value="available">Available</option><option value="limited">Limited</option><option value="unavailable">Unavailable</option>
          </select>
        </Field>
        {!org && (
          <Field label="Initial status" id={`${tid}-st`}>
            <select id={`${tid}-st`} className="input" value={v.status} onChange={(e) => set("status", e.target.value)} data-testid={`${tid}-status-select`}>
              <option value="pending">Pending</option><option value="approved">Approved</option>
            </select>
          </Field>
        )}
      </div>
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy} data-testid={`${tid}-save-button`}>{busy ? "Saving…" : org ? "Save changes" : "Create organization"}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} data-testid={`${tid}-cancel-button`}>Cancel</button>
      </div>
    </form>
  );
}

export function OrgMembers({ orgId }: { orgId: string }) {
  const api = useApi();
  const { data, loading, error, reload } = useLoad<any[]>(`/ops/orgs/${orgId}/members`);
  const patch = async (mid: string, body: any) => {
    try { await api("patch", `/ops/memberships/${mid}`, body); toast.success("Member updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data?.length) return <p className="text-sm text-[var(--muted)]">No members yet. Use Onboarding to record an invitation.</p>;
  return (
    <ul className="space-y-2 text-sm" data-testid={`ops-org-members-${orgId}`}>
      {data.map((m) => (
        <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-[var(--steel)] bg-white p-2">
          <span className="min-w-0 break-all">{m.name} <span className="text-[var(--muted)]">{m.email}</span></span>
          <span className="flex flex-wrap items-center gap-1">
            <StatusBadge value={m.role} /><StatusBadge value={m.status} />
            <button className="btn btn-sm btn-ghost" onClick={() => patch(m.id, { role: m.role === "owner" ? "staff" : "owner" })} data-testid={`ops-member-role-${m.id}`}>{m.role === "owner" ? "Make staff" : "Make owner"}</button>
            <button className="btn btn-sm btn-danger" onClick={() => patch(m.id, { status: m.status === "active" ? "inactive" : "active" })} data-testid={`ops-member-status-${m.id}`}>{m.status === "active" ? "Deactivate" : "Reactivate"}</button>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function FilterBar({ children }: { children: React.ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-[var(--steel)] bg-white p-3">{children}</div>;
}

export const qs = (o: Record<string, string>) => Object.entries(o).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
